import { CreatorLifecycleState } from '../loop/creator-lifecycle';

/**
 * Maps each lifecycle state to the experience a user or creator should see.
 * Product UX definition only — no UI components or routes.
 */
export interface LifecycleUXExperience {
  readonly state: CreatorLifecycleState;
  readonly audience: 'visitor' | 'user' | 'creator';
  readonly primarySurfaces: readonly string[];
  readonly capabilities: readonly string[];
  readonly prompts: readonly string[];
}

export const LIFECYCLE_UX_MAPPING: readonly LifecycleUXExperience[] = [
  {
    state: CreatorLifecycleState.VISITOR,
    audience: 'visitor',
    primarySurfaces: ['public landing', 'public feed preview', 'discover creators'],
    capabilities: ['browse public content', 'view creator profiles', 'sign up or log in'],
    prompts: ['Create an account to follow creators and engage'],
  },
  {
    state: CreatorLifecycleState.USER,
    audience: 'user',
    primarySurfaces: ['public feed', 'discover creators', 'creator profiles'],
    capabilities: ['follow creators', 'view products', 'purchase digital products', 'like posts (optional)'],
    prompts: ['Become a creator to publish content and sell products'],
  },
  {
    state: CreatorLifecycleState.ASPIRING_CREATOR,
    audience: 'creator',
    primarySurfaces: ['studio onboarding', 'public profile (incomplete)'],
    capabilities: ['complete creator profile', 'choose category', 'select subscription tier'],
    prompts: ['Finish onboarding to publish your first post', 'Set up your creator identity'],
  },
  {
    state: CreatorLifecycleState.ACTIVE_CREATOR,
    audience: 'creator',
    primarySurfaces: ['studio dashboard', 'content management', 'public creator profile'],
    capabilities: ['create and publish posts', 'edit profile', 'view basic stats (followers, posts)'],
    prompts: ['Publish consistently to grow visibility', 'Add products when ready to monetize'],
  },
  {
    state: CreatorLifecycleState.MONETIZING_CREATOR,
    audience: 'creator',
    primarySurfaces: ['studio dashboard', 'products dashboard', 'earnings summary'],
    capabilities: [
      'create and publish products',
      'view revenue and purchases',
      'manage subscription plan',
    ],
    prompts: ['Promote products to increase feed visibility', 'Upgrade plan for more studio access'],
  },
  {
    state: CreatorLifecycleState.SCALED_CREATOR,
    audience: 'creator',
    primarySurfaces: ['studio dashboard', 'analytics overview', 'growth insights'],
    capabilities: [
      'view advanced stats',
      'track engagement trends',
      'manage catalog and content at scale',
    ],
    prompts: ['Review growth insights to sustain momentum'],
  },
];

export type LifecycleUXMapping = typeof LIFECYCLE_UX_MAPPING;

/** Lookup UX experience definition for a lifecycle state. */
export function getLifecycleUXExperience(
  state: CreatorLifecycleState,
): LifecycleUXExperience | undefined {
  return LIFECYCLE_UX_MAPPING.find((entry) => entry.state === state);
}
